import { Component } from 'react'
import type { ErrorInfo, ReactNode } from 'react'
import { AlertTriangle } from 'lucide-react'
import './ErrorBoundary.css'

type FallbackRender = (args: { error: Error; reset: () => void }) => ReactNode

type Props = {
  children: ReactNode
  /** Custom fallback. Receives the caught error and a `reset` to retry rendering the subtree. */
  fallback?: ReactNode | FallbackRender
  /** Changing any value in this array resets the boundary (e.g. pass the route path). */
  resetKeys?: unknown[]
  /** Label for the contained region, shown in the default fallback ("This page", "This section"…). */
  regionLabel?: string
  /** Notified after an error is caught — for logging/telemetry. */
  onError?: (error: Error, info: ErrorInfo) => void
}

type State = { error: Error | null }

function shallowDiffer(a: unknown[], b: unknown[]): boolean {
  if (a.length !== b.length) return true
  return a.some((v, i) => !Object.is(v, b[i]))
}

// General-purpose error boundary: catches render/lifecycle errors in its subtree so a single broken
// component can never blank the whole app. The default fallback keeps surrounding chrome (sidebar,
// header) interactive and offers a retry. Auto-resets when `resetKeys` change (e.g. on navigation),
// so moving to another route clears a stale error without a full reload.
export default class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Surface to the console so it still shows up in dev tools / session recordings.
    console.error('ErrorBoundary caught an error:', error, info)
    this.props.onError?.(error, info)
  }

  componentDidUpdate(prev: Props) {
    const { resetKeys } = this.props
    if (this.state.error && prev.resetKeys && resetKeys && shallowDiffer(prev.resetKeys, resetKeys)) {
      this.reset()
    }
  }

  reset = () => this.setState({ error: null })

  render() {
    const { error } = this.state
    if (!error) return this.props.children

    const { fallback, regionLabel = 'This page' } = this.props
    if (typeof fallback === 'function') return (fallback as FallbackRender)({ error, reset: this.reset })
    if (fallback !== undefined) return fallback

    return (
      <div className="error-boundary" role="alert" data-testid="error-boundary-fallback">
        <div className="error-boundary__icon" aria-hidden>
          <AlertTriangle size={28} strokeWidth={1.75} />
        </div>
        <h2 className="error-boundary__title">{regionLabel} hit an unexpected error</h2>
        <p className="error-boundary__message">
          Something went wrong while rendering. You can try again, or use the navigation to go elsewhere.
        </p>
        {error.message && (
          <pre className="error-boundary__detail" data-testid="error-boundary-detail">{error.message}</pre>
        )}
        <div className="error-boundary__actions">
          <button
            type="button"
            className="error-boundary__btn error-boundary__btn--primary"
            onClick={this.reset}
            data-analytics-event="error-retry"
          >
            Try again
          </button>
          <button
            type="button"
            className="error-boundary__btn"
            onClick={() => window.location.reload()}
            data-analytics-event="error-reload"
          >
            Reload page
          </button>
        </div>
      </div>
    )
  }
}
